// Helpers для Web Push на PWA-стороне.
// Используется экраном Settings: запросить разрешение, подписаться, отписаться.

export interface VapidInfo {
  publicKey: string;
  enabled: boolean;
}

export interface AuthedApi {
  get<T>(url: string): Promise<T>;
  post<T>(url: string, body?: unknown): Promise<T>;
  delete<T>(url: string): Promise<T>;
}

export async function isStandalone(): Promise<boolean> {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // @ts-expect-error iOS Safari proprietary
    window.navigator.standalone === true
  );
}

export async function canUsePush(): Promise<boolean> {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

export async function currentPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

export async function ensureRegistration(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

export async function subscribe(api: AuthedApi): Promise<PushSubscription> {
  const vapid = await api.get<VapidInfo>('/api/v1/push/vapid-public-key');
  if (!vapid.enabled || !vapid.publicKey) {
    throw new Error('Server has no VAPID configured');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }
  const reg = await ensureRegistration();
  const expectedKey = urlBase64ToUint8Array(vapid.publicKey);
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    // Если подписка существует, но создана со старым VAPID-ключом
    // (например, ключи на сервере поменялись) — FCM/APNs будет молча
    // отклонять пуши с 403. Принудительно переподписываемся.
    const actualKey = existing.options.applicationServerKey;
    if (!serverKeysEqual(actualKey, expectedKey)) {
      await existing.unsubscribe().catch(() => {});
    } else {
      await api.post('/api/v1/devices/register-push', existing.toJSON());
      return existing;
    }
  }
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: expectedKey as BufferSource,
  });
  await api.post('/api/v1/devices/register-push', sub.toJSON());
  return sub;
}

export async function unsubscribe(api: AuthedApi): Promise<void> {
  const reg = await ensureRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (sub) await sub.unsubscribe();
  await api.delete('/api/v1/devices/register-push').catch(() => {});
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function serverKeysEqual(
  actual: ArrayBuffer | null | undefined,
  expected: Uint8Array,
): boolean {
  if (!actual) return false;
  const a = new Uint8Array(actual);
  if (a.byteLength !== expected.byteLength) return false;
  for (let i = 0; i < a.byteLength; i++) {
    if (a[i] !== expected[i]) return false;
  }
  return true;
}
